// CGCS2000.ts (重写为 CGCS2000 类)

import { proj4 } from 'proj4ts';
import { 
  CGCS2000_GEOGRAPHIC,
  CGCS2000_GK_3DEG_NO_ZONE,
  CGCS2000_GK_3DEG_WITH_ZONE,
  CGCS2000_GK_6DEG_NO_ZONE,
  CGCS2000_GK_6DEG_WITH_ZONE,
  findCoordinateSystemByWkid,
  findCoordinateSystemByName
} from 'wkid';

/**
 * 椭球体参数接口
 */
export interface Ellipsoid {
  name: string;
  semiMajorAxis: number; // 长半轴
  flattening: number;    // 扁率
}

/**
 * 坐标系定义接口
 */
export interface CoordinateSystem {
  wkid: number;
  name: string;
  proj4: string;
  ogcWkt: string;
  esriWkt: string;
  json: any;
}

/**
 * 坐标点类型
 */
export type CoordinatePoint = [number, number];

/**
 * CGCS2000 中国大地坐标系类
 */
export class CGCS2000 {
  private static readonly WKID_RANGE: [number, number] = [4490, 4554];
  
  /**
   * CGCS2000 坐标系的 WKID
   */
  public static readonly WKID = 4490;
  
  /**
   * CGCS2000 地理坐标系定义
   */
  public static readonly GEOGRAPHIC = CGCS2000_GEOGRAPHIC;

  /**
   * 获取 CGCS2000 椭球体参数
   * @returns 椭球体参数
   */
  public static getEllipsoid(): Ellipsoid {
    return {
      name: CGCS2000_GEOGRAPHIC.json.datum.ellipseName,
      semiMajorAxis: CGCS2000_GEOGRAPHIC.json.datum.radius,
      flattening: CGCS2000_GEOGRAPHIC.json.datum.flattening
    };
  }

  /**
   * 根据 WKID 获取坐标系定义
   * @param wkid 坐标系 WKID
   * @returns 坐标系定义或 undefined
   */
  public static getCoordinateSystem(wkid: number): CoordinateSystem | undefined {
    return findCoordinateSystemByWkid(wkid);
  }

  /**
   * 根据名称获取坐标系定义
   * @param name 坐标系名称
   * @returns 坐标系定义或 undefined
   */
  public static getCoordinateSystemByName(name: string): CoordinateSystem | undefined {
    return findCoordinateSystemByName(name);
  }

  /**
   * 验证 WKID 是否为 CGCS2000 系列坐标系
   * @param wkid 坐标系 WKID
   * @returns 是否为 CGCS2000 系列坐标系
   */
  public static isCGCS2000(wkid: number): boolean {
    const [min, max] = CGCS2000.WKID_RANGE;
    return wkid >= min && wkid <= max;
  }

  /**
   * 坐标转换
   * @param sourceWkid 源坐标系 WKID
   * @param targetWkid 目标坐标系 WKID
   * @param coordinates 坐标 [x, y] 或 [lon, lat]
   * @returns 转换后的坐标
   * @throws 当坐标系不支持时抛出错误
   */
  public static transform(sourceWkid: number, targetWkid: number, coordinates: CoordinatePoint): CoordinatePoint {
    // 如果源和目标坐标系相同，直接返回
    if (sourceWkid === targetWkid) {
      return [...coordinates] as CoordinatePoint;
    }

    // 验证源坐标系是否为 CGCS2000 系列
    if (!CGCS2000.isCGCS2000(sourceWkid)) {
      throw new Error(`不支持的源坐标系 WKID: ${sourceWkid}`);
    }

    // 验证目标坐标系是否为 CGCS2000 系列
    if (!CGCS2000.isCGCS2000(targetWkid)) {
      throw new Error(`不支持的目标坐标系 WKID: ${targetWkid}`);
    }

    // 执行坐标转换
    return proj4(coordinates, sourceWkid, targetWkid);
  }

  /**
   * 批量坐标转换
   * @param sourceWkid 源坐标系 WKID
   * @param targetWkid 目标坐标系 WKID
   * @param coordinates 坐标数组
   * @returns 转换后的坐标数组
   */
  public static transformBatch(
    sourceWkid: number, 
    targetWkid: number, 
    coordinates: CoordinatePoint[]
  ): CoordinatePoint[] {
    return coordinates.map(coord => CGCS2000.transform(sourceWkid, targetWkid, coord));
  }

  /**
   * 获取所有 3 度带无带号坐标系定义
   * @returns 3 度带无带号坐标系数组
   */
  public static getGk3DegNoZone(): CoordinateSystem[] {
    return [...CGCS2000_GK_3DEG_NO_ZONE];
  }

  /**
   * 获取所有 3 度带带带号坐标系定义
   * @returns 3 度带带带号坐标系数组
   */
  public static getGk3DegWithZone(): CoordinateSystem[] {
    return [...CGCS2000_GK_3DEG_WITH_ZONE];
  }

  /**
   * 获取所有 6 度带无带号坐标系定义
   * @returns 6 度带无带号坐标系数组
   */
  public static getGk6DegNoZone(): CoordinateSystem[] {
    return [...CGCS2000_GK_6DEG_NO_ZONE];
  }

  /**
   * 获取所有 6 度带带带号坐标系定义
   * @returns 6 度带带带号坐标系数组
   */
  public static getGk6DegWithZone(): CoordinateSystem[] {
    return [...CGCS2000_GK_6DEG_WITH_ZONE];
  }

  /**
   * 根据中央子午线计算 3 度带带号
   * @param centralMeridian 中央子午线
   * @returns 带号
   */
  public static calculate3DegZoneNumber(centralMeridian: number): number {
    return Math.round((centralMeridian + 1.5) / 3);
  }

  /**
   * 根据中央子午线计算 6 度带带号
   * @param centralMeridian 中央子午线
   * @returns 带号
   */
  public static calculate6DegZoneNumber(centralMeridian: number): number {
    return Math.round((centralMeridian + 3) / 6);
  }
}

// 导出 CGCS2000 相关常量
export {
  CGCS2000_GEOGRAPHIC,
  CGCS2000_GK_3DEG_NO_ZONE,
  CGCS2000_GK_3DEG_WITH_ZONE,
  CGCS2000_GK_6DEG_NO_ZONE,
  CGCS2000_GK_6DEG_WITH_ZONE,
  findCoordinateSystemByWkid,
  findCoordinateSystemByName
};
// 默认导出 CGCS2000 类
export default CGCS2000;